import { useRef, useEffect, useState, Fragment } from "react";

import { Dialog, Menu, Transition } from "@headlessui/react";
import { MagnifyingGlassIcon } from "@heroicons/react/20/solid";

import MessageBox from "./MessageBox";
import Notification from "./Notification";
import { CloseOutlined, Preview } from "@mui/icons-material";
import Loading from "./Loading";
import { GetAllUnprocessedEmails } from "./DataServices/UnprocessedEmailService";

export interface IUnprocessedEmail {
    id: string;
    emailContent: string;
    subject: string;
    dateOfExtraction: string;
}

export default function UnprocessedEmails() {
    function classNames(...classes: any[]) {
        return classes.filter(Boolean).join(" ");
    }

    const checkbox = useRef<HTMLInputElement>(null);
    const [checked, setChecked] = useState(false);
    const [indeterminate, setIndeterminate] = useState(false);
    const [selectedEmails, setSelectedEmails] = useState<IUnprocessedEmail[]>([]);
    const [openMessageBox2, setOpenMessageBox2] = useState(false);
    const [openNotification, setOpenNotification] = useState(false);
    const [emails, setEmails] = useState<IUnprocessedEmail[]>([]);
    const [loading, setLoading] = useState(false);
    const [previewEmail, setPreviewEmail] = useState<string | null>(null);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [searchQuery, setSearchQuery] = useState<string>("");
    const [notificationMessageType, setNotificationMessageType] =
        useState("success");

    const handleApartmentSelection = (apartment: IUnprocessedEmail) => {
        if (selectedEmails.includes(apartment)) {
            setSelectedEmails(selectedEmails?.filter((a) => a !== apartment));
        } else {
            setSelectedEmails([...selectedEmails, apartment]);
        }
    };

    function toggleAll() {
        setSelectedEmails(checked || indeterminate ? [] : emails);
        setChecked(!checked && !indeterminate);
        setIndeterminate(false);
    }

    const handleSearch = (query: string) => setSearchQuery(query);
    const filteredEmails = emails.filter((email) =>
        email.subject.toLowerCase().includes(searchQuery.toLowerCase())
    );
    useEffect(() => {
        async function fetchData() {
            try {
                setLoading(true);
                const data = await GetAllUnprocessedEmails();
                // console.log(data)
                setEmails(data);
                setLoading(false);
            } catch (error) {
                setLoading(false);
                console.error("Error fetching data:", error);
            }
        }
        fetchData();
    }, []);

    return (
        <>
            <div className="px-4 sm:px-6 lg:px-8 relative mt-14">
                <div className="sm:flex sm:items-center">
                    <div className="sm:flex-auto">
                        <h1 className="text-base font-semibold leading-6 text-gray-900">
                            Unprocessed Emails
                        </h1>
                        <p className="mt-2 text-sm text-gray-700">
                            A list of all the Emails the were not processed by the system.
                        </p>
                    </div>
                    {/* <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none flex space-x-2">
            <button
              onClick={() => {}}
              className="bg-gradient-to-r from-purple-500 to-indigo-600 text-white font-semibold text-sm py-2 px-4 rounded-full shadow hover:from-purple-600 hover:to-indigo-700 transition duration-300 ease-in-out transform hover:scale-[1.02] active:scale-100"
            >
              Reprocess Emails
            </button>
          </div> */}

                    <Notification
                        isOpen={openNotification}
                        message={notificationMessage}
                        messageType={notificationMessageType}
                        setIsOpen={setOpenNotification}
                    />
                </div>
                <div className="order-1 md:order-2 relative grid justify-items-end flex-grow items-stretch focus-within:z-10">
                    <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center pl-2 ">
                        <MagnifyingGlassIcon
                            className="h-5 w-5 text-gray-400 dark:text-gray-500"
                            aria-hidden="true"
                        />
                    </div>
                    <input
                        type="text"
                        name="search"
                        id="search"
                        className="block w-60 rounded-md border-0 text-gray-900 ring-1 ring-gray-300 placeholder:text-gray-400 sm:text-md bg-transparent"
                        placeholder="Search By Subject"
                        onChange={(e) => {
                            handleSearch(e.target.value);
                        }}
                    />
                </div>
                <div className="mt-5 flow-root">
                    <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                        <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                            <div className="w-full">
                                <table className="min-w-full table-fixed divide-y divide-gray-300 border rounded-md shadow">
                                    <thead className="text-center bg-gray-100">
                                        <tr>
                                            <th scope="col" className="relative px-7 sm:w-12 sm:px-6">
                                                <input
                                                    type="checkbox"
                                                    className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                    ref={checkbox}
                                                    checked={checked}
                                                    onChange={toggleAll}
                                                />
                                            </th>

                                            <th
                                                scope="col"
                                                className="whitespace-nowrap p-3 text-left text-sm font-semibold text-gray-900"
                                            >
                                                Subject
                                            </th>

                                            <th
                                                scope="col"
                                                className="whitespace-nowrap p-3 text-left text-sm font-semibold text-gray-900"
                                            >
                                                Date of Extraction
                                            </th>

                                            <th
                                                scope="col"
                                                className="whitespace-nowrap p-3 text-left text-sm font-semibold text-gray-900"
                                            >
                                                Email Body
                                            </th>

                                            <th
                                                scope="col"
                                                className="p-3 text-left text-sm font-semibold text-gray-900"
                                            >
                                                <span className="sr-only">Preview</span>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="w-full divide-y divide-gray-200 bg-white">
                                        {
                                            !loading && filteredEmails.sort((a, b) => {
                                                const timeA = new Date(a.dateOfExtraction).getTime() || 0;  // Fallback to 0 if invalid
                                                const timeB = new Date(b.dateOfExtraction).getTime() || 0;
                                                return timeB - timeA;
                                            }).map((email, i) => (
                                                <tr
                                                    key={i}
                                                    className={
                                                        selectedEmails.includes(email)
                                                            ? "bg-gray-50"
                                                            : undefined
                                                    }
                                                >
                                                    <td className="relative px-7 sm:w-12 sm:px-6">
                                                        <input
                                                            type="checkbox"
                                                            className="absolute left-4 top-1/2 -mt-2 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                                                            value={
                                                                email.dateOfExtraction
                                                                    ? email.dateOfExtraction
                                                                    : ""
                                                            }
                                                            checked={selectedEmails.includes(email)}
                                                            onChange={() => handleApartmentSelection(email)}
                                                        />
                                                    </td>

                                                    <td
                                                        className={classNames(
                                                            "w-fit whitespace-nowrap p-3 text-sm font-medium",
                                                            selectedEmails.includes(email)
                                                                ? "text-indigo-600"
                                                                : "text-gray-900"
                                                        )}
                                                    >
                                                        {email.subject}
                                                    </td>

                                                    <td className="w-fit whitespace-nowrap p-3 text-sm text-gray-500">
                                                        {email.dateOfExtraction
                                                            ? new Date(
                                                                email.dateOfExtraction
                                                            ).toLocaleDateString()
                                                            : ""}
                                                    </td>

                                                    <td
                                                        scope="row"
                                                        className="max-w-sm w-fit truncate p-3 text-sm text-gray-500"
                                                    >
                                                        {email.emailContent}
                                                    </td>

                                                    <td>
                                                        <Menu as="div" className="flex-none">
                                                            <Menu.Button
                                                                onClick={() =>
                                                                    setPreviewEmail(email.emailContent)
                                                                }
                                                                className="whitespace-nowrap w-fit p-2 text-sm text-gray-500 hover:text-blue-600 flex items-center justify-center active:scale-95"
                                                            >
                                                                <span className="sr-only">Open options</span>
                                                                <Preview className="w-10" aria-hidden="true" />
                                                            </Menu.Button>
                                                        </Menu>
                                                    </td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </table>
                                {loading &&
                                    <div className="w-full border rounded-b-md shadow flex items-center justify-center">
                                        <Loading />
                                    </div>
                                }
                                {(filteredEmails.length === 0 && !loading) &&
                                    <div className="w-full border rounded-b-md p-2 shadow flex items-center justify-center">
                                        No Unprocessed Emails
                                    </div>
                                }
                            </div>
                        </div>
                    </div>
                </div>
                <Transition.Root show={previewEmail !== null} as={Fragment}>
                    <Dialog
                        as="div"
                        className="relative z-10 w-[60%] h-[70%]"
                        onClose={() => setPreviewEmail(null)}
                    >
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0"
                            enterTo="opacity-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100"
                            leaveTo="opacity-0"
                        >
                            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                        </Transition.Child>

                        <div className="fixed inset-0 z-10 w-screen">
                            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0 ">
                                <Transition.Child
                                    as="div"
                                    enter="ease-out duration-300"
                                    enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                    enterTo="opacity-100 translate-y-0 sm:scale-100"
                                    leave="ease-in duration-200"
                                    leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                    leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                >
                                    <Dialog.Panel className="relative transform overflow-hidden rounded-md  text-left shadow-xl transition-all w-[650px] h-[80vh] ">
                                        <div className="w-full ">
                                            <div className="flex items-center justify-between p-2 bg-blue-400 rounded-t-lg ">
                                                <h1 className="font-medium ">Unprocessed Email</h1>
                                                <button onClick={() => setPreviewEmail(null)}>
                                                    <CloseOutlined
                                                        className="cursor-pointer active:translate-y-[1px]"
                                                        fontSize="small"
                                                    />
                                                </button>
                                            </div>
                                            <div className="!text-gray-600 inline-block h-[calc(80vh-20px)] w-full overflow-y-auto">
                                                <div
                                                    className="w-full"
                                                    dangerouslySetInnerHTML={{
                                                        __html: previewEmail?.replace("bodyText", "") ?? "",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        {loading && <Loading />}
                                    </Dialog.Panel>
                                </Transition.Child>
                            </div>
                        </div>
                    </Dialog>
                </Transition.Root>
                <MessageBox
                    title="Apartments - Listing Deletion"
                    message="This apartment has some available appointments. Are you sure you want to delete this?"
                    isOpen={openMessageBox2}
                    setIsOpen={setOpenMessageBox2}
                    okButtonText="Ok"
                    cancelButtonText="Cancel"
                    okButtonFunction={() => { }}
                    cancelButtonFunction={() => setOpenMessageBox2(false)}
                    okButtonStyle={{ backgroundColor: "#ff2c2c" }}
                    iconColor={{ color: "#ff2c2c" }}
                />
            </div>
        </>
    );
}